import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import authService from '../../services/authService';

// MUI Components
import {
  Box,
  Typography,
  Container,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  FormHelperText
} from '@mui/material';

// MUI Icons
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  LockReset as LockResetIcon
} from '@mui/icons-material';

const RequiredPasswordChange = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Password validation
  const [passwordErrors, setPasswordErrors] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
    match: false
  });

  useEffect(() => {
    // Check if we should redirect user back to original destination
    const checkAndRedirect = async () => {
      try {
        const user = await authService.getCurrentUser();
        if (!user) {
          navigate('/login', { replace: true });
          return;
        }
        
        // If no password reset required and no force flag, redirect
        if (!user.passwordResetRequired && !sessionStorage.getItem('forcePasswordReset')) {
          const redirectTo = sessionStorage.getItem('redirectAfterPasswordChange') || '/dashboard';
          navigate(redirectTo, { replace: true });
        }
      } catch (err) {
        console.error('Error checking user status:', err);
      }
    };
    
    checkAndRedirect();
  }, [navigate]);

  // Validate password as user types
  const validatePassword = (password) => {
    return {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[^A-Za-z0-9]/.test(password),
      match: password === confirmPassword
    };
  };

  const handleNewPasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);
    setPasswordErrors({
      ...validatePassword(value),
      match: value === confirmPassword
    });
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);
    setPasswordErrors({
      ...passwordErrors,
      match: newPassword === value
    });
  };

  const isValidForm = () => {
    return (
      currentPassword &&
      newPassword &&
      confirmPassword &&
      Object.values(passwordErrors).every(value => value === true) &&
      newPassword !== currentPassword
    );
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword === currentPassword) {
      setError('New password must be different from the temporary password');
      return;
    }

    if (!isValidForm()) {
      setError('Please correct the password errors before submitting');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      await authService.changePassword(currentPassword, newPassword);
      
      setSuccess(true);
      sessionStorage.removeItem('forcePasswordReset');
      
      // Wait briefly to show success message before navigating
      setTimeout(() => {
        const redirectTo = sessionStorage.getItem('redirectAfterPasswordChange') || 
                          (location.state?.from || '/dashboard');
        
        // Clear the stored redirect path
        sessionStorage.removeItem('redirectAfterPasswordChange');
        
        navigate(redirectTo, { replace: true });
      }, 1500);
      
    } catch (err) {
      console.error('Password change error:', err);
      
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Failed to change password. Please check your current password and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <LockResetIcon color="primary" sx={{ fontSize: 48, mb: 2 }} />
          <Typography component="h1" variant="h5" gutterBottom>
            Password Change Required
          </Typography>
          
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Your account is using a temporary password. You must create a new password before continuing.
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
              Password changed successfully! Redirecting...
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleChangePassword} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Current/Temporary Password"
              type={showCurrentPassword ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      edge="end"
                    >
                      {showCurrentPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              label="New Password"
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={handleNewPasswordChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              label="Confirm New Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              error={confirmPassword !== '' && !passwordErrors.match}
              helperText={confirmPassword !== '' && !passwordErrors.match ? 'Passwords do not match' : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            
            <Box sx={{ mt: 2, mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Password Requirements:
              </Typography>
              <FormHelperText 
                error={!passwordErrors.length} 
                sx={{ display: 'block', color: passwordErrors.length ? 'success.main' : undefined }}
              >
                • At least 8 characters {passwordErrors.length && '✓'}
              </FormHelperText>
              <FormHelperText 
                error={!passwordErrors.uppercase} 
                sx={{ display: 'block', color: passwordErrors.uppercase ? 'success.main' : undefined }}
              >
                • At least one uppercase letter {passwordErrors.uppercase && '✓'}
              </FormHelperText>
              <FormHelperText 
                error={!passwordErrors.lowercase} 
                sx={{ display: 'block', color: passwordErrors.lowercase ? 'success.main' : undefined }}
              >
                • At least one lowercase letter {passwordErrors.lowercase && '✓'}
              </FormHelperText>
              <FormHelperText 
                error={!passwordErrors.number} 
                sx={{ display: 'block', color: passwordErrors.number ? 'success.main' : undefined }}
              >
                • At least one number {passwordErrors.number && '✓'}
              </FormHelperText>
              <FormHelperText 
                error={!passwordErrors.special} 
                sx={{ display: 'block', color: passwordErrors.special ? 'success.main' : undefined }}
              >
                • At least one special character {passwordErrors.special && '✓'}
              </FormHelperText>
            </Box>
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || !isValidForm()}
              sx={{ mt: 3, mb: 2 }}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LockResetIcon />}
            >
              {loading ? 'Changing Password...' : 'Change Password'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default RequiredPasswordChange;
